import React from 'react';

function CookiePolicy() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold">
                <span className="text-red-600">Cookie</span>
                <span className="text-blue-900"> Policy</span>
              </h1>
              <p className="text-sm text-gray-600 mt-1">Freedom Contracting Group</p>
            </div>
            <a
              href="/"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Back to Home"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
          
          {/* Last Updated */}
          <div className="mb-8 pb-6 border-b border-gray-200">
            <p className="text-sm text-gray-500">Last Updated: January 20, 2026</p>
          </div>

          {/* What Are Cookies */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">What Are Cookies?</h2>
            <p className="text-gray-700 leading-relaxed">
              Cookies are small text files stored on your device when you visit a website. They help websites function properly and provide information about how the site is used.
            </p>
          </section>

          {/* How We Use Cookies */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">How We Use Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use cookies for limited purposes:
            </p>
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-blue-900 mb-1">Essential Cookies</h3>
                    <p className="text-gray-700">
                      Required for the website to function properly, such as enabling page navigation and form submission.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-6">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-blue-900 mb-1">Analytics Cookies</h3>
                    <p className="text-gray-700">
                      Help us understand how visitors use our website so we can improve it. This data is aggregated and anonymous.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* What We Don't Use */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">What We Don't Use</h2>
            <div className="bg-green-50 border-l-4 border-green-600 rounded-r-lg p-6">
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>No advertising or remarketing cookies</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>No behavioral profiling or tracking across other websites</span>
                </li>
                <li className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>No selling or sharing of cookie data with advertisers</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Managing Cookies */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Managing Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You can control cookies through your browser settings. Most browsers allow you to:
            </p>
            <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4 mb-4">
              <li>View which cookies are stored</li>
              <li>Delete cookies</li>
              <li>Block cookies entirely</li>
            </ul>
            <div className="p-4 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
              <p className="text-gray-700">
                <strong>Note:</strong> Blocking cookies may affect how the website functions.
              </p>
            </div>
          </section>

          {/* Changes */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Changes to This Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you have questions about cookies, please contact us:
            </p>
            <div className="bg-slate-50 rounded-lg p-6">
              <div className="flex items-start gap-3">
                <svg className="w-5 h-5 text-blue-900 flex-shrink-0 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
                </svg>
                <div>
                  <p className="font-medium text-blue-900">Email:</p>
                  <a href="mailto:privacy@freedomcg.net" className="text-red-600 hover:underline">
                    privacy@freedomcg.net
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* Related Policies */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-blue-900 mb-4">Related Policies</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <a 
                href="/privacy-policy"
                className="flex items-center gap-3 bg-slate-50 hover:bg-blue-50 rounded-lg p-4 transition-colors group"
              >
                <div className="w-10 h-10 bg-blue-900 group-hover:bg-red-600 rounded-full flex items-center justify-center transition-colors">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"></path>
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-blue-900 group-hover:text-red-600 transition-colors">Privacy Policy</p>
                  <p className="text-sm text-gray-600">How we protect your data</p>
                </div>
              </a>
              <a 
                href="/terms"
                className="flex items-center gap-3 bg-slate-50 hover:bg-blue-50 rounded-lg p-4 transition-colors group"
              >
                <div className="w-10 h-10 bg-blue-900 group-hover:bg-red-600 rounded-full flex items-center justify-center transition-colors">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-blue-900 group-hover:text-red-600 transition-colors">Terms of Use</p>
                  <p className="text-sm text-gray-600">Website terms and conditions</p>
                </div>
              </a>
            </div>
          </section>

          {/* Back to Top Button */}
          <div className="mt-12 pt-8 border-t border-gray-200 text-center">
            <button
              onClick={scrollToTop}
              className="inline-flex items-center gap-2 bg-blue-900 text-white hover:bg-red-600 font-medium py-3 px-6 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 10l7-7m0 0l7 7m-7-7v18"></path>
              </svg>
              Back to Top
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicy;